<script lang="ts">
  import type { IntegrationDetails, GetExternalContactsResponse } from "../../helpers/types";
  import { track } from "../../helpers/mixpanel";
  import { getExternalContacts, getIntegrations } from "../../helpers/integrations-api";

  import SupplyLines from "./SupplyLines.svelte";
  import ConnectAccount from "./ConnectAccount.svelte";
  import Notifications from "./Notifications.svelte";
  import Site from "./Site.svelte";
  import IntegrationFlows from "./IntegrationFlows.svelte";

  import LoadingSpinner from "../../components/LoadingSpinner.svelte";
  import { externalContactsStore, integrationHasSites } from "../../stores/integrations-store";
  import { disconnect } from "../../helpers/integrations-api";
  import TrackingCategories from "./TrackingCategories.svelte";
  import CloseIcon from "../icons/CloseIcon.svelte";
  import { selectedBuyer } from "../../stores/buyer";

  export let integration: IntegrationDetails;
  export let disconnected = (platformName: string) => {};
  export let onClose: () => void;

  let externalContacts: GetExternalContactsResponse | null;

  externalContactsStore.subscribe((value) => {
    externalContacts = value;
  });

  const handleDisconnect = (val: IntegrationDetails | null) => {
    if (val === null) {
      return;
    }

    track("integration_disconnect_click", {
      buyer_id: $selectedBuyer?.id,
      buyer_name: $selectedBuyer?.name,
      integration_id: integration.id,
      platform: integration.platform,
      type: "management",
    });

    disconnect(val).then((res) => {
      disconnected("");
    });
  };

  const handleAuthComplete = (isOauth: boolean) => {
    if (isOauth) {
      track("integration_oauth_closed", {
        buyer_id: $selectedBuyer?.id,
        buyer_name: $selectedBuyer?.name,
        integration_id: integration.id,
        platform: integration.platform,
        type: "management",
      });
    }

    getExternalContacts(integration.id);
    getIntegrations(integration.buyer_id);
  };
</script>

<div class="container">
  <div class="header">
    <button on:click={onClose}><CloseIcon /></button>
  </div>
  {#if !externalContacts}
    <div class="loading-container">
      <div class="loading-spinner">
        <LoadingSpinner size={50} />
      </div>
    </div>
  {:else}
    <ConnectAccount
      {integration}
      isConnected={!!externalContacts?.authenticated}
      onPopupClosed={(isOauth) => handleAuthComplete(isOauth)}
    />
    {#if integrationHasSites(integration?.platform)}
      <Site
        {integration}
        contacts={externalContacts?.result || null}
        authenticated={externalContacts?.authenticated}
      />
    {/if}
    <SupplyLines
      {integration}
      contacts={externalContacts?.result || null}
      authenticated={externalContacts?.authenticated}
    />
    <Notifications {integration} authenticated={externalContacts?.authenticated} />
    {#if integration?.platform == "Xero"}
      <TrackingCategories
        {integration}
        contacts={externalContacts?.result || null}
        authenticated={externalContacts?.authenticated}
      />
      <IntegrationFlows {integration} />
    {/if}

    <div class="disconnect-container">
      <div class="disconnect">
        would you rather do this manually? <button
          class="btn"
          on:click={() => handleDisconnect(integration)}>Disconnect</button
        >
      </div>
    </div>
  {/if}
</div>

<style>
  .container {
    border-top: 1px solid;
    padding: 18px;
    border-color: var(--rekki-neutral-01);
  }

  .header {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
  }

  .header button {
    border: 0 none;
    background-color: transparent;
    padding-bottom: 24px;
    margin: 0;
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 75vh;
    width: 100%;
  }

  .disconnect .btn {
    border-radius: 100px;
    padding: 10px 20px;
    color: var(--rekki-light);
    border: none;
    justify-self: flex-end;
    font-family: "REKKI-OCD";
    white-space: nowrap;
    text-transform: uppercase;
    margin: auto;
    font-size: 14px;
    margin-left: 18px;
    background-color: var(--rekki-neutral-01);
    color: var(--rekki-dark);
  }

  .disconnect-container {
    border-radius: 8px;
    background-color: white;
    margin-bottom: 18px;
    display: flex;
    justify-content: end;
    padding: 18px;
  }
</style>
